import { twoPlans, threePlans, fourPlans, frenchPlans } from './options.js'

// All 11 logical steps (used for STEP_FIELDS completion tracking)
export const STEPS = [
  { id: 1,  title: 'Student'    },
  { id: 2,  title: 'School'     },
  { id: 3,  title: 'Skills'     },
  { id: 4,  title: 'Language'   },
  { id: 5,  title: 'French'     },
  { id: 6,  title: 'Math'       },
  { id: 7,  title: 'Science'    },
  { id: 8,  title: 'Hist./Geo.' },
  { id: 9,  title: 'Health/PE'  },
  { id: 10, title: 'Arts'       },
]

// Subject step IDs - steps 4–9 are owned by the "Subjects" parent node
export const SUBJECT_STEP_IDS = new Set([4, 5, 6, 7, 8, 9])

// Top-level indicator nodes (6 nodes - Subjects is a parent for 4–9)
export const TOP_STEPS = [
  { id: 1,          label: 'Student'  },
  { id: 2,          label: 'School'   },
  { id: 3,          label: 'Skills'   },
  { id: 'subjects', label: 'Subjects', stepIds: [4, 5, 6, 7, 8, 9], jumpTo: 4 },
  { id: 10,         label: 'Arts'     },
]

// Sub-row nodes - always visible beneath the top row
export const SUB_STEPS = [
  { id: 4, label: 'Language' },
  { id: 5, label: 'French'   },
  { id: 6, label: 'Math'     },
  { id: 7, label: 'Science'  },
  { id: 8, label: 'Hist./Geo.' },
  { id: 9, label: 'H&PE'     },
]

export const STEP_FIELDS = {
  1:  ['studentName', 'oen', 'date', 'grade', 'teacher'],
  2:  ['board', 'school', 'daysAbsent', 'totalDaysAbsent', 'timesLate', 'totalTimesLate'],
  3:  ['responsibility1','organization1','independentWork1','collaboration1','initiative1','selfRegulation1',
       'responsibility2','organization2','independentWork2','collaboration2','initiative2','selfRegulation2',
       'strengthsNextStepsLSWH'],
  4:  ['languagePlan', 'languageGrade1', 'languageGrade2'],
  5:  ['frenchPlan', 'frenchListeningGrade1', 'frenchListeningGrade2'],
  6:  ['mathematicsPlan', 'mathematicsGrade1', 'mathematicsGrade2', 'mathematicsComments'],
  7:  ['scienceAndTechnologyPlan', 'scienceAndTechnologyGrade1', 'scienceAndTechnologyGrade2'],
  8:  ['historyPlan', 'historyGrade1', 'historyGrade2',
       'geographyPlan', 'geographyGrade1', 'geographyGrade2'],
  9:  ['healthEducationGrade1', 'healthEducationGrade2',
       'physicalEducationGrade1', 'physicalEducationGrade2'],
  10: ['artsHeader', 'arts1Grade1', 'arts1Grade2']
}

export const FIELD_LABELS = {
  date: 'Date',
  studentName: 'Student Name',
  oen: 'OEN',
  grade: 'Grade',
  gradeInSeptember: 'Grade in September',
  teacher: 'Teacher',
  board: 'School Board',
  school: 'School',
  address1: 'Address Line 1',
  address2: 'Address Line 2',
  principal: 'Principal',
  telephone: 'Telephone',
  daysAbsent: 'Days Absent',
  totalDaysAbsent: 'Total Days Absent',
  timesLate: 'Times Late',
  totalTimesLate: 'Total Times Late',
  responsibility1: 'Responsibility (Term 1)',
  organization1: 'Organization (Term 1)',
  independentWork1: 'Independent Work (Term 1)',
  collaboration1: 'Collaboration (Term 1)',
  initiative1: 'Initiative (Term 1)',
  selfRegulation1: 'Self-Regulation (Term 1)',
  responsibility2: 'Responsibility (Term 2)',
  organization2: 'Organization (Term 2)',
  independentWork2: 'Independent Work (Term 2)',
  collaboration2: 'Collaboration (Term 2)',
  initiative2: 'Initiative (Term 2)',
  selfRegulation2: 'Self-Regulation (Term 2)',
  strengthsNextStepsLSWH: 'Strengths & Next Steps (Learning Skills)',
  languagePlan: 'Language - Plan',
  languageGrade1: 'Language - Grade (Term 1)',
  languageMedian1: 'Language - Median (Term 1)',
  languageGrade2: 'Language - Grade (Term 2)',
  languageMedian2: 'Language - Median (Term 2)',
  languageComments: 'Language - Comments',
  nativeLanguage: 'Native Language Name',
  nativeLanguagePlan: 'Native Language - Plan',
  nativeLanguageGrade1: 'Native Language - Grade (Term 1)',
  nativeLanguageMedian1: 'Native Language - Median (Term 1)',
  nativeLanguageGrade2: 'Native Language - Grade (Term 2)',
  nativeLanguageMedian2: 'Native Language - Median (Term 2)',
  nativeLanguageComments: 'Native Language - Comments',
  frenchPlan: 'French - Plan',
  frenchListeningPlan: 'French Listening - Plan',
  frenchListeningGrade1: 'French Listening - Grade (Term 1)',
  frenchListeningMedian1: 'French Listening - Median (Term 1)',
  frenchListeningGrade2: 'French Listening - Grade (Term 2)',
  frenchListeningMedian2: 'French Listening - Median (Term 2)',
  frenchSpeakingPlan: 'French Speaking - Plan',
  frenchSpeakingGrade1: 'French Speaking - Grade (Term 1)',
  frenchSpeakingMedian1: 'French Speaking - Median (Term 1)',
  frenchSpeakingGrade2: 'French Speaking - Grade (Term 2)',
  frenchSpeakingMedian2: 'French Speaking - Median (Term 2)',
  frenchReadingPlan: 'French Reading - Plan',
  frenchReadingGrade1: 'French Reading - Grade (Term 1)',
  frenchReadingMedian1: 'French Reading - Median (Term 1)',
  frenchReadingGrade2: 'French Reading - Grade (Term 2)',
  frenchReadingMedian2: 'French Reading - Median (Term 2)',
  frenchWritingPlan: 'French Writing - Plan',
  frenchWritingGrade1: 'French Writing - Grade (Term 1)',
  frenchWritingMedian1: 'French Writing - Median (Term 1)',
  frenchWritingGrade2: 'French Writing - Grade (Term 2)',
  frenchWritingMedian2: 'French Writing - Median (Term 2)',
  frenchComments: 'French - Comments',
  mathematicsPlan: 'Mathematics - Plan',
  mathematicsGrade1: 'Mathematics - Grade (Term 1)',
  mathematicsMedian1: 'Mathematics - Median (Term 1)',
  mathematicsGrade2: 'Mathematics - Grade (Term 2)',
  mathematicsMedian2: 'Mathematics - Median (Term 2)',
  mathematicsComments: 'Mathematics - Comments',
  scienceAndTechnologyPlan: 'Science & Technology - Plan',
  scienceAndTechnologyGrade1: 'Science & Technology - Grade (Term 1)',
  scienceAndTechnologyMedian1: 'Science & Technology - Median (Term 1)',
  scienceAndTechnologyGrade2: 'Science & Technology - Grade (Term 2)',
  scienceAndTechnologyMedian2: 'Science & Technology - Median (Term 2)',
  scienceAndTechnologyComments: 'Science & Technology - Comments',
  historyPlan: 'History - Plan',
  historyGrade1: 'History - Grade (Term 1)',
  historyMedian1: 'History - Median (Term 1)',
  historyGrade2: 'History - Grade (Term 2)',
  historyMedian2: 'History - Median (Term 2)',
  historyComments: 'History - Comments',
  geographyPlan: 'Geography - Plan',
  geographyGrade1: 'Geography - Grade (Term 1)',
  geographyMedian1: 'Geography - Median (Term 1)',
  geographyGrade2: 'Geography - Grade (Term 2)',
  geographyMedian2: 'Geography - Median (Term 2)',
  geographyComments: 'Geography - Comments',
  healthEducationPlan: 'Health Education - Plan',
  healthEducationGrade1: 'Health Education - Grade (Term 1)',
  healthEducationMedian1: 'Health Education - Median (Term 1)',
  healthEducationGrade2: 'Health Education - Grade (Term 2)',
  healthEducationMedian2: 'Health Education - Median (Term 2)',
  physicalEducationPlan: 'Physical Education - Plan',
  physicalEducationGrade1: 'Physical Education - Grade (Term 1)',
  physicalEducationMedian1: 'Physical Education - Median (Term 1)',
  physicalEducationGrade2: 'Physical Education - Grade (Term 2)',
  physicalEducationMedian2: 'Physical Education - Median (Term 2)',
  healthAndPhysicalEducationComments: 'Health & PE - Comments',
  artsHeader: 'Arts Header',
  arts1: 'Arts 1 Name',
  arts1Plan: 'Arts 1 - Plan',
  arts1Grade1: 'Arts 1 - Grade (Term 1)',
  arts1Median1: 'Arts 1 - Median (Term 1)',
  arts1Grade2: 'Arts 1 - Grade (Term 2)',
  arts1Median2: 'Arts 1 - Median (Term 2)',
  arts2: 'Arts 2 Name',
  arts2Plan: 'Arts 2 - Plan',
  arts2Grade1: 'Arts 2 - Grade (Term 1)',
  arts2Median1: 'Arts 2 - Median (Term 1)',
  arts2Grade2: 'Arts 2 - Grade (Term 2)',
  arts2Median2: 'Arts 2 - Median (Term 2)',
  arts3: 'Arts 3 Name',
  arts3Plan: 'Arts 3 - Plan',
  arts3Grade1: 'Arts 3 - Grade (Term 1)',
  arts3Median1: 'Arts 3 - Median (Term 1)',
  arts3Grade2: 'Arts 3 - Grade (Term 2)',
  arts3Median2: 'Arts 3 - Median (Term 2)',
  arts4: 'Arts 4 Name',
  arts4Plan: 'Arts 4 - Plan',
  arts4Grade1: 'Arts 4 - Grade (Term 1)',
  arts4Median1: 'Arts 4 - Median (Term 1)',
  arts4Grade2: 'Arts 4 - Grade (Term 2)',
  arts4Median2: 'Arts 4 - Median (Term 2)',
  artsComments: 'Arts - Comments',
  optionalStudies: 'Optional Studies Name',
  optionalStudiesPlan: 'Optional Studies - Plan',
  optionalStudiesGrade1: 'Optional Studies - Grade (Term 1)',
  optionalStudiesGrade2: 'Optional Studies - Grade (Term 2)',
  optionalStudiesComments: 'Optional Studies - Comments',
}

// Subject card configs used by the renderStep function in Page.jsx
// median1Field / median2Field are optional - when present, SubjectCard shows median inputs
export const SUBJECT_CARDS = [
  { id: 4,  title: 'Language',             planField: 'languagePlan',             grade1Field: 'languageGrade1',             median1Field: 'languageMedian1',             grade2Field: 'languageGrade2',             median2Field: 'languageMedian2',             commentsField: 'languageComments',             plans: twoPlans,   nameField: null },
  { id: 4,  title: 'Native Language',      planField: 'nativeLanguagePlan',       grade1Field: 'nativeLanguageGrade1',       median1Field: 'nativeLanguageMedian1',       grade2Field: 'nativeLanguageGrade2',       median2Field: 'nativeLanguageMedian2',       commentsField: 'nativeLanguageComments',       plans: twoPlans,   nameField: 'nativeLanguage' },
  { id: 5,  title: 'French',               planField: 'frenchPlan',               grade1Field: null,                         median1Field: null,                          grade2Field: null,                         median2Field: null,                          commentsField: 'frenchComments',               plans: frenchPlans, nameField: null },
  { id: 6,  title: 'Mathematics',          planField: 'mathematicsPlan',          grade1Field: 'mathematicsGrade1',          median1Field: 'mathematicsMedian1',          grade2Field: 'mathematicsGrade2',          median2Field: 'mathematicsMedian2',          commentsField: 'mathematicsComments',          plans: threePlans, nameField: null },
  { id: 7,  title: 'Science & Technology', planField: 'scienceAndTechnologyPlan', grade1Field: 'scienceAndTechnologyGrade1', median1Field: 'scienceAndTechnologyMedian1', grade2Field: 'scienceAndTechnologyGrade2', median2Field: 'scienceAndTechnologyMedian2', commentsField: 'scienceAndTechnologyComments', plans: threePlans, nameField: null },
  { id: 8,  title: 'History',              planField: 'historyPlan',              grade1Field: 'historyGrade1',              median1Field: 'historyMedian1',              grade2Field: 'historyGrade2',              median2Field: 'historyMedian2',              commentsField: 'historyComments',              plans: fourPlans,  nameField: null },
  { id: 8,  title: 'Geography',            planField: 'geographyPlan',            grade1Field: 'geographyGrade1',            median1Field: 'geographyMedian1',            grade2Field: 'geographyGrade2',            median2Field: 'geographyMedian2',            commentsField: 'geographyComments',            plans: fourPlans,  nameField: null },
  { id: 9,  title: 'Health Education',     planField: 'healthEducationPlan',      grade1Field: 'healthEducationGrade1',      median1Field: 'healthEducationMedian1',      grade2Field: 'healthEducationGrade2',      median2Field: 'healthEducationMedian2',      commentsField: null,                           plans: threePlans, nameField: null },
  { id: 9,  title: 'Physical Education',   planField: 'physicalEducationPlan',    grade1Field: 'physicalEducationGrade1',    median1Field: 'physicalEducationMedian1',    grade2Field: 'physicalEducationGrade2',    median2Field: 'physicalEducationMedian2',    commentsField: null,                           plans: threePlans, nameField: null },
]

export const ARTS_ROWS = [
  { nameField: 'arts1', planField: 'arts1Plan', grade1Field: 'arts1Grade1', median1Field: 'arts1Median1', grade2Field: 'arts1Grade2', median2Field: 'arts1Median2', placeholder: 'e.g. Visual Arts' },
  { nameField: 'arts2', planField: 'arts2Plan', grade1Field: 'arts2Grade1', median1Field: 'arts2Median1', grade2Field: 'arts2Grade2', median2Field: 'arts2Median2', placeholder: 'e.g. Music' },
  { nameField: 'arts3', planField: 'arts3Plan', grade1Field: 'arts3Grade1', median1Field: 'arts3Median1', grade2Field: 'arts3Grade2', median2Field: 'arts3Median2', placeholder: 'e.g. Drama' },
  { nameField: 'arts4', planField: 'arts4Plan', grade1Field: 'arts4Grade1', median1Field: 'arts4Median1', grade2Field: 'arts4Grade2', median2Field: 'arts4Median2', placeholder: 'e.g. Dance' },
]
